import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { BoltIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { addFastTrackContact } from '../../../../services/firebase';
import ContactSelector from './ContactSelector';

const FastTrackActions = ({ jobId, userId, onActionComplete }) => {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Connection state
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactCompany, setContactCompany] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactSignalStrength, setContactSignalStrength] = useState(2);
  const [contactAvailableTimes, setContactAvailableTimes] = useState('');
  const [contactNotes, setContactNotes] = useState('');
  const [originalContact, setOriginalContact] = useState(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  
  // Invite state
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCompany, setInviteCompany] = useState('');
  const [inviteNotes, setInviteNotes] = useState('');
  const [originalInviteContact, setOriginalInviteContact] = useState(null);
  const [showInviteOverwriteConfirm, setShowInviteOverwriteConfirm] = useState(false);
  const [pendingInviteSubmitData, setPendingInviteSubmitData] = useState(null);

  const handleConnectContactSelect = (contact) => {
    setOriginalContact(contact);
    setContactFirstName(contact.firstName || '');
    setContactLastName(contact.lastName || '');
    setContactEmail(contact.email || '');
    setContactCompany(contact.company || '');
    setContactPhone(contact.phone || '');
    setContactSignalStrength(contact.signalStrength || 2);
    setContactAvailableTimes(contact.availableTimes || '');
    setContactNotes(contact.notes || '');
  };

  const handleInviteContactSelect = (contact) => {
    setOriginalInviteContact(contact);
    setInviteFirstName(contact.firstName || '');
    setInviteLastName(contact.lastName || '');
    setInviteEmail(contact.email || '');
    setInviteCompany(contact.company || '');
    setInviteNotes(contact.notes || '');
  };
  
  const resetConnectForm = () => {
    setContactFirstName('');
    setContactLastName('');
    setContactEmail('');
    setContactCompany('');
    setContactPhone('');
    setContactSignalStrength(2);
    setContactAvailableTimes('');
    setContactNotes('');
    setOriginalContact(null);
    setPendingSubmitData(null);
  };

  const resetInviteForm = () => {
    setInviteFirstName('');
    setInviteLastName('');
    setInviteEmail('');
    setInviteCompany('');
    setInviteNotes('');
    setOriginalInviteContact(null);
    setPendingInviteSubmitData(null);
  };
  
  const isContactDataChanged = () => {
    if (!originalContact) return false;
    
    return (
      contactFirstName !== (originalContact.firstName || '') ||
      contactLastName !== (originalContact.lastName || '') ||
      contactEmail !== (originalContact.email || '') ||
      contactCompany !== (originalContact.company || '') ||
      contactPhone !== (originalContact.phone || '') ||
      contactSignalStrength !== (originalContact.signalStrength || 2) ||
      contactAvailableTimes !== (originalContact.availableTimes || '') ||
      contactNotes !== (originalContact.notes || '')
    );
  };
  
  const isInviteContactDataChanged = () => {
    if (!originalInviteContact) return false;
    
    return (
      inviteFirstName !== (originalInviteContact.firstName || '') ||
      inviteLastName !== (originalInviteContact.lastName || '') ||
      inviteEmail !== (originalInviteContact.email || '') ||
      inviteCompany !== (originalInviteContact.company || '') ||
      inviteNotes !== (originalInviteContact.notes || '')
    );
  };
  
  const submitFastTrackConnect = async (useOriginalData = false) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let contactData;
      
      if (useOriginalData && originalContact) {
        // Use the original contact data but keep the fast track request info
        contactData = {
          ...originalContact,
          jobId,
          userId,
          type: 'fast_track_connect',
          status: 'requested',
          requestedAt: new Date()
        };
      } else {
        // Use the current form data
        contactData = {
          firstName: contactFirstName,
          lastName: contactLastName,
          email: contactEmail,
          company: contactCompany,
          phone: contactPhone,
          signalStrength: contactSignalStrength,
          availableTimes: contactAvailableTimes,
          notes: contactNotes,
          jobId,
          userId,
          type: 'fast_track_connect',
          status: 'requested',
          requestedAt: new Date()
        };
        
        // If there's an existing contact ID, include it
        if (originalContact && originalContact.id) {
          contactData.id = originalContact.id;
        }
      }
      
      const result = await addFastTrackContact(contactData);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to add fast track contact');
      }
      
      setShowConnectModal(false);
      onActionComplete && onActionComplete('connect');
      
      // Reset form
      resetConnectForm();
    } catch (error) {
      console.error("Error adding fast track connect:", error);
      setError(error.message || "An error occurred while processing your request");
    } finally {
      setIsLoading(false);
      setShowOverwriteConfirm(false);
      setPendingSubmitData(null);
    }
  };
  
  const handleFastTrackConnect = async (e) => {
    e.preventDefault();
    
    // If data has changed from an existing contact, ask for confirmation
    if (originalContact && originalContact.id && isContactDataChanged()) {
      setShowOverwriteConfirm(true);
      return;
    }
    
    // Otherwise proceed with submit
    await submitFastTrackConnect();
  };
  
  const submitFastTrackInvite = async (useOriginalData = false) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let contactData;
      
      if (useOriginalData && originalInviteContact) {
        // Use the original contact data but keep the fast track invite info
        contactData = {
          ...originalInviteContact,
          jobId,
          userId,
          type: 'fast_track_invite',
          status: 'invited',
          invitedAt: new Date()
        };
      } else {
        // Use the current form data
        contactData = {
          firstName: inviteFirstName,
          lastName: inviteLastName,
          email: inviteEmail,
          company: inviteCompany,
          notes: inviteNotes,
          jobId,
          userId,
          type: 'fast_track_invite',
          status: 'invited',
          invitedAt: new Date()
        };
        
        // If there's an existing contact ID, include it
        if (originalInviteContact && originalInviteContact.id) {
          contactData.id = originalInviteContact.id;
        }
      }
      
      const result = await addFastTrackContact(contactData);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to add fast track invite');
      }
      
      setShowInviteModal(false);
      onActionComplete && onActionComplete('invite');
      
      // Reset form
      resetInviteForm();
    } catch (error) {
      console.error("Error adding fast track invite:", error);
      setError(error.message || "An error occurred while processing your request");
    } finally {
      setIsLoading(false);
      setShowInviteOverwriteConfirm(false);
      setPendingInviteSubmitData(null);
    }
  };
  
  const handleFastTrackInvite = async (e) => {
    e.preventDefault();
    
    // If data has changed from an existing contact, ask for confirmation
    if (originalInviteContact && originalInviteContact.id && isInviteContactDataChanged()) {
      setShowInviteOverwriteConfirm(true);
      return;
    }
    
    // Otherwise proceed with submit
    await submitFastTrackInvite();
  };
  
  return (
    <div className="mt-3 pt-2 border-t border-gray-100">
      <h4 className="font-medium text-sm text-gray-700 mb-2">Fast Track Actions</h4>
      <div className="flex flex-wrap gap-2">
        <button 
          type="button"
          onClick={() => setShowConnectModal(true)}
          className="inline-flex items-center text-xs px-3 py-2 rounded-md bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors shadow-sm"
        >
          <BoltIcon className="h-4 w-4 mr-1.5" />
          Fast Track Connect
        </button>
        
        <button 
          type="button"
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center text-xs px-3 py-2 rounded-md bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors shadow-sm"
        >
          <EnvelopeIcon className="h-4 w-4 mr-1.5" />
          Fast Track Invite
        </button>
      </div>
      
      {/* Fast Track Connect Modal */}
      <Transition.Root show={showConnectModal} as={Fragment}>
        <Dialog 
          as="div" 
          className="relative z-50" 
          onClose={() => {
            setShowConnectModal(false);
            resetConnectForm();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      onClick={() => {
                        setShowConnectModal(false);
                        resetConnectForm();
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                      <BoltIcon className="h-6 w-6 text-purple-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Fast Track Connect
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Add contact details for the person who can fast track this candidate. By saving this contact, they'll be added as a fast track connection for this job.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-md px-3 py-2 mt-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5 text-purple-600" aria-hidden="true" />
                      </div>
                      <div className="ml-3 flex-1 md:flex md:justify-between">
                        <p className="text-xs text-purple-700">
                          Add contact details for the person who can fast track this candidate. You'll be sending a fast track request to this contact.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact selector */}
                  <form onSubmit={handleFastTrackConnect} className="space-y-4 mt-4">
                    <ContactSelector 
                      jobId={jobId}
                      userId={userId}
                      onSelectContact={handleConnectContactSelect}
                    />
                    
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="contact-first-name" className="block text-sm font-medium leading-6 text-gray-900">
                          First name
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="contact-first-name"
                            id="contact-first-name"
                            required
                            value={contactFirstName}
                            onChange={(e) => setContactFirstName(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="contact-last-name" className="block text-sm font-medium leading-6 text-gray-900">
                          Last name
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="contact-last-name"
                            id="contact-last-name"
                            required
                            value={contactLastName}
                            onChange={(e) => setContactLastName(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-4">
                        <label htmlFor="contact-email" className="block text-sm font-medium leading-6 text-gray-900">
                          Email address
                        </label>
                        <div className="mt-2">
                          <input
                            type="email"
                            name="contact-email"
                            id="contact-email"
                            required
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label htmlFor="contact-company" className="block text-sm font-medium leading-6 text-gray-900">
                          Company
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="contact-company"
                            id="contact-company"
                            value={contactCompany}
                            onChange={(e) => setContactCompany(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-4">
                        <label htmlFor="contact-phone" className="block text-sm font-medium leading-6 text-gray-900">
                          Phone number
                        </label>
                        <div className="mt-2">
                          <input
                            type="tel"
                            name="contact-phone"
                            id="contact-phone"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="e.g. (555) 123-4567"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label htmlFor="contact-signal-strength" className="block text-sm font-medium leading-6 text-gray-900">
                          Response likelihood
                        </label>
                        <p className="text-xs text-gray-500 mt-1">How likely is this contact to respond to fast track requests?</p>
                        <div className="mt-2 flex items-center space-x-4">
                          {[1, 2, 3].map((strength) => (
                            <div key={strength} className="flex items-center">
                              <input
                                id={`signal-strength-${strength}`}
                                name="contact-signal-strength"
                                type="radio"
                                checked={contactSignalStrength === strength}
                                onChange={() => setContactSignalStrength(strength)}
                                className="h-4 w-4 border-gray-300 text-purple-600 focus:ring-purple-600"
                              />
                              <label htmlFor={`signal-strength-${strength}`} className="ml-2 block text-sm font-medium leading-6 text-gray-900">
                                {strength === 1 ? 'Low' : strength === 2 ? 'Medium' : 'High'}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {contactAvailableTimes && (
                        <div className="sm:col-span-6">
                          <label htmlFor="contact-available-times" className="block text-sm font-medium leading-6 text-gray-900">
                            Available times (optional)
                          </label>
                          <div className="mt-2">
                            <input
                              type="text"
                              name="contact-available-times"
                              id="contact-available-times"
                              value={contactAvailableTimes}
                              onChange={(e) => setContactAvailableTimes(e.target.value)}
                              placeholder="e.g. Tuesdays 2-4pm, Thursdays 10-11am"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="sm:col-span-6">
                        <label htmlFor="contact-notes" className="block text-sm font-medium leading-6 text-gray-900">
                          Notes
                        </label>
                        <div className="mt-2">
                          <textarea
                            id="contact-notes"
                            name="contact-notes"
                            rows={3}
                            value={contactNotes}
                            onChange={(e) => setContactNotes(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                            placeholder="Any additional information about this contact..."
                          />
                        </div>
                      </div>
                    </div>
                    
                    {error && (
                      <div className="rounded-md bg-red-50 p-4 mt-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>{error}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 sm:col-start-2"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : 'Send Fast Track Request'}
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        onClick={() => {
                          setShowConnectModal(false);
                          resetConnectForm();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
      {/* Overwrite Confirmation Dialog */}
      <Transition.Root show={showOverwriteConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowOverwriteConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Update Contact Information?
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You've made changes to an existing contact. Would you like to update their information or keep the original details?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 sm:ml-3 sm:w-auto"
                      onClick={() => submitFastTrackConnect(false)}
                    >
                      Update Contact
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => submitFastTrackConnect(true)}
                    >
                      Keep Original
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
      {/* Fast Track Invite Modal */}
      <Transition.Root show={showInviteModal} as={Fragment}>
        <Dialog 
          as="div" 
          className="relative z-50" 
          onClose={() => {
            setShowInviteModal(false);
            resetInviteForm();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                      onClick={() => {
                        setShowInviteModal(false);
                        resetInviteForm();
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                      <EnvelopeIcon className="h-6 w-6 text-teal-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Fast Track Invite
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Invite the user to fast track their job application by contacting this person directly.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-teal-50 border border-teal-200 rounded-md px-3 py-2 mt-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5 text-teal-600" aria-hidden="true" />
                      </div>
                      <div className="ml-3 flex-1 md:flex md:justify-between">
                        <p className="text-xs text-teal-700">
                          The user will be notified to contact this person directly to fast track their application for this job.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact selector */}
                  <form onSubmit={handleFastTrackInvite} className="space-y-4 mt-4">
                    <ContactSelector 
                      jobId={jobId}
                      userId={userId}
                      onSelectContact={handleInviteContactSelect}
                    />
                    
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="invite-first-name" className="block text-sm font-medium leading-6 text-gray-900">
                          First name
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="invite-first-name"
                            id="invite-first-name"
                            required
                            value={inviteFirstName}
                            onChange={(e) => setInviteFirstName(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="invite-last-name" className="block text-sm font-medium leading-6 text-gray-900">
                          Last name
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="invite-last-name"
                            id="invite-last-name"
                            required
                            value={inviteLastName}
                            onChange={(e) => setInviteLastName(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-4">
                        <label htmlFor="invite-email" className="block text-sm font-medium leading-6 text-gray-900">
                          Email address
                        </label>
                        <div className="mt-2">
                          <input
                            type="email"
                            name="invite-email"
                            id="invite-email"
                            required
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label htmlFor="invite-company" className="block text-sm font-medium leading-6 text-gray-900">
                          Company
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="invite-company"
                            id="invite-company"
                            value={inviteCompany}
                            onChange={(e) => setInviteCompany(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label htmlFor="invite-notes" className="block text-sm font-medium leading-6 text-gray-900">
                          Notes (optional)
                        </label>
                        <div className="mt-2">
                          <textarea
                            id="invite-notes"
                            name="invite-notes"
                            rows={3}
                            value={inviteNotes}
                            onChange={(e) => setInviteNotes(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6"
                            placeholder="Any additional instructions for the user..."
                          />
                        </div>
                      </div>
                    </div>
                    
                    {error && (
                      <div className="rounded-md bg-red-50 p-4 mt-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>{error}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 sm:col-start-2"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : 'Send Fast Track Invite'}
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        onClick={() => {
                          setShowInviteModal(false);
                          resetInviteForm();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
      {/* Invite Overwrite Confirmation Dialog */}
      <Transition.Root show={showInviteOverwriteConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowInviteOverwriteConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Update Contact Information?
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You've made changes to an existing contact. Would you like to update their information or keep the original details?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 sm:ml-3 sm:w-auto"
                      onClick={() => submitFastTrackInvite(false)}
                    >
                      Update Contact
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => submitFastTrackInvite(true)}
                    >
                      Keep Original
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default FastTrackActions; 